import React, { createContext, useContext, useCallback, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { useDispatch, useSelector } from 'react-redux';
import { AuthContext } from '../Header/auth - context';
import { osveziUlazneBadge } from '../custom/badgeFakture';

export const ChatSocketContext = createContext(null);

// Ako ikad budeš hteo da zvuk bude iz fajla (npr. pravi WhatsApp zvuk), stavi MP3 u public/ folder i zameni playNotifSound sa:
// new Audio('/notif.mp3').play().catch(() => {});

const playNotifSound = () => {
    try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const tone = (freq, startTime, duration) => {
            const osc  = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.type = 'sine';
            osc.frequency.value = freq;
            gain.gain.setValueAtTime(0.35, startTime);
            gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
            osc.start(startTime);
            osc.stop(startTime + duration);
        };
        tone(440, ctx.currentTime,        0.12);
        tone(660, ctx.currentTime + 0.13, 0.15);
    } catch {}
};

export const ChatSocketProvider = ({ children }) => {
    const auth      = useContext(AuthContext);
    const dispatch  = useDispatch();
    const myId      = useSelector(s => s.user?.userId);
    const myIdRef   = useRef(myId);
    const socketRef = useRef(null);
    /* Isti socket, ali kao STANJE. Ref ne izaziva ponovni render, pa komponenta koja
       kači listenere u useEffect-u (CallContext) nikad ne sazna da je socket
       nastao: njen efekat se izvrši jednom, pri montiranju — PRE ovog efekta, jer
       React pokreće efekte dece pre roditelja — zatekne null i odustane. Tako se
       `call-incoming` uredno stizao u pregledač, ali ga niko nije slušao, i poziv
       nikad nije zazvonio. Zavisnost od ovog stanja ponovo pokreće takve efekte. */
    const [aktivniSocket, setAktivniSocket] = useState(null);
    const [onlineUsers, setOnlineUsers] = useState([]);
    /* Prisustvo sa tri stanja: { "OPERATER:5": { stanje, poslednjaAktivnost } }.
       `onlineUsers` ostaje netaknut — on je i dalje „ima li živ socket" i na njemu
       visi avatar sa zelenim prstenom na spisku razgovora. */
    const [prisustvo, setPrisustvo] = useState({});

    useEffect(() => { myIdRef.current = myId; }, [myId]);

    /* ── Da li čovek STVARNO nešto radi ─────────────────────────────────────

       Otvorena kartica nije isto što i čovek za stolom. Granicu može da povuče
       samo pregledač, jer samo on vidi miša, tastaturu i da li je kartica u prvom
       planu — server bi morao da nagađa iz saobraćaja, a chat šalje poruke i kad
       korisnik samo gleda u ekran.

       `ODZIV_MS` je prigušenje: aktivnost se javlja najviše jednom u minut, pa
       pomeranje miša ne pravi bujicu poruka. Prag mora biti ISTI kao
       `PRISUSTVO_AWAY_MS` na serveru (socket/chatSocket.js). */
    const PRAG_AWAY_MS = 15 * 60 * 1000;
    const ODZIV_MS = 60 * 1000;

    useEffect(() => {
        const socket = socketRef.current;
        if (!auth.token || !socket) return undefined;

        let poslednjiJav = 0;
        let tajmer = null;

        const naIdle = () => { try { socket.emit('presence:idle'); } catch {} };

        const aktivan = () => {
            const sada = Date.now();
            /* Tajmer se pomera na SVAKU radnju; poruka ide prigušeno. */
            clearTimeout(tajmer);
            tajmer = setTimeout(naIdle, PRAG_AWAY_MS);
            if (sada - poslednjiJav < ODZIV_MS) return;
            poslednjiJav = sada;
            try { socket.emit('presence:active'); } catch {}
        };

        /* `visibilitychange` je ovde ključan: prelazak na drugu karticu ili
           minimizovanje prozora je upravo ono „ne koristi aplikaciju". Vraćanje na
           karticu se broji kao radnja. */
        const naVidljivost = () => {
            if (document.visibilityState === 'visible') aktivan();
            else naIdle();
        };

        const dogadjaji = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'wheel'];
        dogadjaji.forEach((d) => window.addEventListener(d, aktivan, { passive: true }));
        window.addEventListener('focus', aktivan);
        document.addEventListener('visibilitychange', naVidljivost);

        aktivan();   // otvaranje app-a je radnja

        return () => {
            clearTimeout(tajmer);
            dogadjaji.forEach((d) => window.removeEventListener(d, aktivan));
            window.removeEventListener('focus', aktivan);
            document.removeEventListener('visibilitychange', naVidljivost);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auth.token, aktivniSocket]);

    /* Tačan broj ulaznih faktura koje čekaju ovog korisnika — uvek se čita iz baze,
       nikad ne uvećava lokalno. Lokalno uvećanje bi se razišlo sa stvarnim stanjem
       čim se faktura proveri u drugom tabu ili je neko prebaci na drugog čoveka. */
    const osveziUlazneZaMene = useCallback(() => {
        osveziUlazneBadge(auth.token, dispatch);
    }, [auth.token, dispatch]);

    useEffect(() => {
        if (!auth.token) return;

        /* Početno stanje oznake — bez ovoga bi se broj pojavio tek na prvu dodelu
           primljenu preko socket-a, a faktura dodeljena dok korisnik nije bio
           prijavljen ne bi se nigde videla. */
        osveziUlazneZaMene();

        // Fetch initial unread count from DB on login
        fetch(process.env.REACT_APP_BACKEND_URL + '/chat/getUnreadCount', {
            method:  'POST',
            headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
            body:    JSON.stringify({}),
        })
            .then(r => r.ok ? r.json() : null)
            .then(data => { if (data) dispatch({ type: 'CHAT_UNREAD_SET', value: data.unread }); })
            .catch(() => {});

        const socketUrl = process.env.REACT_APP_BACKEND_URL.replace(/\/API\/?$/, '');
        const socket = io(socketUrl, {
            auth:       { token: auth.token },
            /* Websocket prvi, ali sa rezervom na polling — iza nekih korporativnih
               proxy-ja i VPN-ova websocket ne prolazi, pa bi chat ostao mrtav bez
               ijedne poruke o grešci. */
            transports: ['websocket', 'polling'],
        });

        socket.on('connect_error', () => {});

        // New message — bump unread only when not on /chat page (Chat.js handles it internally)
        // "Ja" sam uvek OPERATER u ovom kontekstu (web app) — poređenje mora proveriti i tip_korisnika,
        // jer auth.Users.id i auth.MobilniNalog.id mogu numerički da se poklope.
        socket.on('new-message', (msg) => {
            const isMine = msg.id_korisnik === myIdRef.current && (msg.tip_korisnika || 'OPERATER') === 'OPERATER';
            if (!isMine) {
                playNotifSound();
                if (window.location.pathname !== '/chat') {
                    dispatch({ type: 'CHAT_UNREAD_INC' });
                }
            }
        });

        socket.on('online-users', (list) => setOnlineUsers(list));

        /* Tri stanja prisustva (zeleno/zuto/sivo). Server šalje SAMO one koji imaju
           živ socket; ko nije u spisku je offline. */
        socket.on('presence:stanje', (lista) => {
            const mapa = {};
            for (const x of (Array.isArray(lista) ? lista : [])) {
                mapa[x.kljuc] = { stanje: x.stanje, poslednjaAktivnost: x.poslednjaAktivnost };
            }
            setPrisustvo(mapa);
        });
        /* Zatečeno stanje pri priključivanju — bez ovoga bi novi prozor video prazno
           dok se nekome ne promeni status. */
        socket.emit('presence:trazi');

        // Live izmena prava pristupa — admin je snimio nova prava za ovog korisnika.
        // Osveži store (Sidebar/rute reaguju odmah); App.js guard izbacuje iz modula ako je izgubljen 'view'.
        socket.on('permissions:update', (d) => {
            dispatch({ type: 'PERMISSIONS', value: Array.isArray(d?.permissions) ? d.permissions : [] });
        });

        /* Ulazna faktura mi je dodeljena na proveru. Isti obrazac kao `plan:new`:
           server javi da se nešto promenilo, klijent povuče TAČAN broj. Broj se ne
           uvećava lokalno jer bi se razišao sa bazom čim se faktura proveri u drugom
           tabu ili je neko prebaci na drugog čoveka. */
        socket.on('ulazna-faktura-dodeljena', (d) => {
            osveziUlazneZaMene();
            playNotifSound();
            /* Toast ide kroz prozor: kontekst nema svoj Toast, a dodela treba da se
               vidi sa bilo kog ekrana. Ko sluša, prikaže; ko ne, ništa se ne lomi. */
            try {
                window.dispatchEvent(new CustomEvent('tms:ulazna-dodeljena', { detail: d || {} }));
            } catch {}
        });

        // Live badge tiketa: neko mi je dodelio zadatak → osveži broj novih tiketa (bez ulaska na tablu)
        socket.on('plan:new', () => {
            fetch(process.env.REACT_APP_BACKEND_URL + '/plan/novi_count', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
            })
                .then(r => r.ok ? r.json() : null)
                .then(d => {
                    if (!d) return;
                    dispatch({ type: 'PLAN_NOVI_SET', value: d.count || 0 });
                    dispatch({ type: 'PLAN_NEPROCITANI_SET', value: d.neprocitani || 0 });
                })
                .catch(() => {});
            playNotifSound();
        });

        /* Nov komentar na zadatku — osvezi brojac nepročitanih bez ulaska na
           tablu. Ista logika kao za nove tikete iznad. */
        socket.on('plan:comment', () => {
            fetch(process.env.REACT_APP_BACKEND_URL + '/plan/novi_count', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
            })
                .then(r => r.ok ? r.json() : null)
                .then(d => {
                    if (!d) return;
                    dispatch({ type: 'PLAN_NOVI_SET', value: d.count || 0 });
                    dispatch({ type: 'PLAN_NEPROCITANI_SET', value: d.neprocitani || 0 });
                })
                .catch(() => {});
            playNotifSound();
        });

        socketRef.current = socket;
        setAktivniSocket(socket);

        return () => {
            socket.disconnect();
            socketRef.current = null;
            setAktivniSocket(null);
        };
    }, [auth.token, dispatch]);

    return (
        <ChatSocketContext.Provider value={{ socketRef, socket: aktivniSocket, onlineUsers, prisustvo }}>
            {children}
        </ChatSocketContext.Provider>
    );
};
